
import moment from 'moment';

import React, { Component } from 'react';
import styled from 'styled-components';
import { withSnackbar } from 'notistack';

import Layout from 'components/Dashboard/Layout';
import { FilterProvider, FilterContext } from 'core/FilterProvider';
import OrderListItems from 'components/Order/ListItems';
import OrderDetail from 'components/Order/Detail';
import PlanningListHeader from './ListHeader';
import PlanningListItem from './ListItem';
import PlanningFreeVehicles from './FreeVehicles';
import { Drawer } from 'ui/Drawer';
import { PlanningDndProvider } from './Dnd';
import {
  changeOrderBinding,
  clearOrderBinding,
  findBindingForVehicle,
  getOrderBindingSearchParams,
  getOrderTractorUnitId,
  getVehicleTractorId,
  searchBindings,
} from './changeBinding';


const StyledLayoutContent = styled.div`
  min-height: 0;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

const Column = styled.div`
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 20px 30px 30px;
  
  &:first-child {
    border-right: 1px solid #EDEDED;
  }

  .MuiTableContainer-root {
    overflow-x: hidden;
    max-width: 100%;
  }

  .MuiTable-root {
    table-layout: fixed;
    width: 100%;
  }

  .MuiTableCell-root {
    min-width: 0 !important;
    overflow: hidden;
    word-break: break-word;
  }
  
  @media (max-width: 1279px) {
    padding: 10px;
  }
`;


class PlanningSelection extends Component {

  constructor(props) {
    super(props);

    this.state = {
      assigning: false,
      assignedVehicleIds: [],
    };

    this.ordersListRef = React.createRef();
    this.freeVehiclesRef = React.createRef();
  }

  componentDidMount() {
    document.title = 'Планирование — Подбор ТС';
  }

  handleOrdersLoaded = (items) => {
    this.setState({
      assignedVehicleIds: (items || []).map(getOrderTractorUnitId).filter(Boolean),
    });
  };

  assignVehicle = (order, vehicle) => {
    const tractorId = getVehicleTractorId(vehicle);
    if (this.state.assigning || !tractorId || getOrderTractorUnitId(order) === tractorId) {
      return;
    }

    const searchParams = getOrderBindingSearchParams(order);
    if (!searchParams) {
      this.props.enqueueSnackbar('У перевозки не указана дата начала', { variant: 'error' });
      return;
    }

    this.setState((state) => ({
      assigning: true,
      assignedVehicleIds: state.assignedVehicleIds.includes(tractorId)
        ? state.assignedVehicleIds
        : state.assignedVehicleIds.concat(tractorId),
    }));

    searchBindings(searchParams)
      .then((results) => {
        const match = findBindingForVehicle(results, vehicle);
        if (!match) {
          return Promise.reject({
            response: { data: { detail: 'Нет свободной связки для этой перевозки' } },
          });
        }
        return changeOrderBinding(order, match);
      })
      .then(() => {
        this.props.enqueueSnackbar('Связка обновлена', { variant: 'success' });
        if (this.ordersListRef.current) {
          this.ordersListRef.current.reload();
        }
        if (this.freeVehiclesRef.current) {
          this.freeVehiclesRef.current.loadItems();
        }
      })
      .catch((error) => {
        const data = error.response && error.response.data;
        let message = 'Не удалось обновить связку';
        if (data) {
          if (data.all_text_errors) {
            message = data.all_text_errors;
          } else if (data.schedule) {
            message = Array.isArray(data.schedule) ? data.schedule[0] : data.schedule;
          } else if (data.detail) {
            message = data.detail;
          }
        }
        this.props.enqueueSnackbar(message, { variant: 'error' });
        if (this.ordersListRef.current) {
          this.ordersListRef.current.reload();
        }
        if (this.freeVehiclesRef.current) {
          this.freeVehiclesRef.current.loadItems();
        }
      })
      .finally(() => {
        this.setState({ assigning: false });
      });
  };

  clearBinding = (order) => {
    if (this.state.assigning) {
      return;
    }

    const tractorId = getOrderTractorUnitId(order);
    this.setState((state) => ({
      assigning: true,
      assignedVehicleIds: tractorId
        ? state.assignedVehicleIds.filter(id => id !== tractorId)
        : state.assignedVehicleIds,
    }));

    clearOrderBinding(order)
      .then(() => {
        this.props.enqueueSnackbar('Связка снята', { variant: 'success' });
        if (this.ordersListRef.current) {
          this.ordersListRef.current.reload();
        }
        if (this.freeVehiclesRef.current) {
          this.freeVehiclesRef.current.loadItems();
        }
      })
      .catch((error) => {
        const data = error.response && error.response.data;
        let message = 'Не удалось убрать связку';
        if (data) {
          if (data.all_text_errors) {
            message = data.all_text_errors;
          } else if (data.detail) {
            message = data.detail;
          }
        }
        this.props.enqueueSnackbar(message, { variant: 'error' });
        if (this.ordersListRef.current) {
          this.ordersListRef.current.reload();
        }
        if (this.freeVehiclesRef.current) {
          this.freeVehiclesRef.current.loadItems();
        }
      })
      .finally(() => {
        this.setState({ assigning: false });
      });
  };

  getPreviewId = () => {
    const match = this.props.location.pathname.match(/\/preview\/(\d+)/);
    return match ? match[1] : undefined;
  };

  openPreview = (item) => {
    this.props.history.push(`/planning/selection/preview/${item.id}`);
  };

  closePreview = () => {
    this.props.history.push('/planning/selection');
  };

  setDateStart = (context, date) => {
    this.setState({ assignedVehicleIds: [] });
    context.setFilterValue('dateStart', date ? moment(date) : moment());
  };

  render() {
    const previewId = this.getPreviewId();
    const listPath = '/planning/selection';

    return (
      <FilterProvider
        storageKey={'PlanningSelectionDesk'}
        filterDefaults={{
          dateStart: moment()
        }}
      >
        <FilterContext.Consumer>
          {context => {
            const { filter } = context;
            const dateStart = moment(filter.dateStart);

            return (
              <Layout rows={'auto 1fr'}>
                <PlanningListHeader
                  view={'selection'}
                  history={this.props.history}
                  filter={filter}
                  onDateChange={(value) => this.setDateStart(context, value)}
                />

                <PlanningDndProvider
                  disabled={this.state.assigning}
                  onDropVehicle={this.assignVehicle}
                >
                  <StyledLayoutContent>
                    <Column>
                      <OrderListItems
                        key={dateStart.format('YYYY-MM-DD')}
                        ref={this.ordersListRef}
                        date={dateStart}
                        url={'planning/list2'}
                        itemApiUrl={'planning'}
                        itemBasePath={'/planning'}
                        ItemComponent={PlanningListItem}
                        previewId={previewId}
                        listPath={listPath}
                        onPreview={this.openPreview}
                        onClosePreview={this.closePreview}
                        onItemsLoaded={this.handleOrdersLoaded}
                        itemProps={{
                          dropDisabled: this.state.assigning,
                          onClearBinding: this.clearBinding,
                        }}
                      />
                    </Column>
                    <Column>
                      <PlanningFreeVehicles
                        key={dateStart.format('YYYY-MM-DD')}
                        ref={this.freeVehiclesRef}
                        date={dateStart}
                        disabled={this.state.assigning}
                        excludeVehicleIds={this.state.assignedVehicleIds}
                      />
                    </Column>
                  </StyledLayoutContent>
                </PlanningDndProvider>

                <Drawer
                  open={!!previewId}
                  onClose={this.closePreview}
                  width={'450px'}
                  disableEscapeKeyDown={true}
                >
                  <OrderDetail
                    id={previewId}
                    onClose={this.closePreview}
                    apiUrl={'planning'}
                    basePath={'/planning'}
                    fromPath={`${listPath}/preview/${previewId}`}
                  />
                </Drawer>
              </Layout>
            );
          }}
        </FilterContext.Consumer>
      </FilterProvider>
    );
  }
}

export default withSnackbar(PlanningSelection);
